<template>
  <div class="ipromise-league">
    <section class="hero">
      <figure class="hero-visual" data-aos="fade-in">
        <img
          :src="leagueSoccer"
          alt="7-a-side football league action"
          class="hero-visual__img"
          width="1920"
          height="1080"
          decoding="async"
          fetchpriority="high"
        />
      </figure>

      <div class="hero-intro">
        <p class="hero-eyebrow" data-aos="fade-down">
          <i class="fa-solid fa-trophy" aria-hidden="true" />
          IPromise League
        </p>
        <h1 class="hero-title" data-aos="fade-up" data-aos-delay="80">
          7-a-side Football League
        </h1>
        <p class="hero-sub" data-aos="fade-up" data-aos-delay="140">
          Ten teams, two groups, round-robin group play, then semi-finals and a final—with room for a
          third-place playoff or charity exhibition.
        </p>
      </div>
    </section>

    <section class="highlights" aria-label="League highlights">
      <div
        v-for="(h, i) in highlights"
        :key="h.label"
        class="highlight-pill"
        data-aos="zoom-in"
        :data-aos-delay="i * 90"
      >
        <i :class="h.icon" aria-hidden="true" />
        <span>{{ h.label }}</span>
      </div>
    </section>

    <section class="section league-gallery" aria-label="League imagery">
      <div class="gallery-row" data-aos="fade-up">
        <figure class="gallery-card">
          <img :src="leagueSoccer" alt="7-a-side football" loading="lazy" decoding="async" />
          <figcaption>7-a-side on the pitch</figcaption>
        </figure>
        <figure class="gallery-card">
          <img :src="leagueTrophy" alt="IPromise League trophy" loading="lazy" decoding="async" />
          <figcaption>Champions lift the cup</figcaption>
        </figure>
        <figure class="gallery-card">
          <img :src="leagueTrophy2" alt="League silverware" loading="lazy" decoding="async" />
          <figcaption>Glory on the line</figcaption>
        </figure>
      </div>
    </section>

    <section class="section features-section">
      <header class="section-head" data-aos="fade-up">
        <h2>Tournament specs</h2>
        <p>Everything you need to know before kickoff.</p>
      </header>

      <div class="feature-grid">
        <article
          v-for="(f, i) in specCards"
          :key="f.title"
          class="feature-card"
          data-aos="fade-up"
          :data-aos-delay="i * 70"
        >
          <div class="feature-icon-wrap">
            <i :class="f.icon" aria-hidden="true" />
          </div>
          <h3>{{ f.title }}</h3>
          <p>{{ f.desc }}</p>
        </article>
      </div>
    </section>

    <section id="league-format" ref="formatSection" class="split-showcase" data-aos="fade-up">
      <div class="split-visual split-visual--soccer" :style="splitSoccerStyle" aria-hidden="true" />
      <div class="split-copy">
        <h2>Competition format</h2>
        <ul class="split-list">
          <li v-for="(s, i) in formatPoints" :key="i">
            <i class="fa-solid fa-circle-check" aria-hidden="true" />
            <span>{{ s }}</span>
          </li>
        </ul>
      </div>
    </section>

    <section class="section bracket-section">
      <header class="section-head" data-aos="fade-up">
        <h2>Groups &amp; knockout path</h2>
        <p>Round-robin inside each group, then the road to the final.</p>
      </header>

      <div class="groups-knockout" data-aos="fade-up">
        <div class="group-panel">
          <h3><span class="group-badge group-a">A</span> Group A</h3>
          <p class="group-meta">5 teams · 4 matches each (round-robin)</p>
          <ul class="group-list">
            <li v-for="n in 5" :key="'a' + n">Team slot {{ n }}</li>
          </ul>
        </div>
        <div class="group-panel">
          <h3><span class="group-badge group-b">B</span> Group B</h3>
          <p class="group-meta">5 teams · 4 matches each (round-robin)</p>
          <ul class="group-list">
            <li v-for="n in 5" :key="'b' + n">Team slot {{ n }}</li>
          </ul>
        </div>
      </div>

      <div class="knockout-flow" data-aos="fade-up" data-aos-delay="80">
        <div class="knockout-stage">
          <h4><i class="fa-solid fa-shuffle" aria-hidden="true" /> Semi-finals</h4>
          <p>Top 2 from each group advance.</p>
          <ul class="knockout-matches">
            <li><strong>Semi 1</strong> — 1st Group A vs 2nd Group B</li>
            <li><strong>Semi 2</strong> — 1st Group B vs 2nd Group A</li>
          </ul>
        </div>
        <div class="knockout-arrow" aria-hidden="true">
          <i class="fa-solid fa-arrow-down" />
        </div>
        <div class="knockout-stage knockout-final">
          <h4><i class="fa-solid fa-crown" aria-hidden="true" /> Final</h4>
          <p>Semi-final winners meet for the title.</p>
        </div>
        <div class="knockout-arrow" aria-hidden="true">
          <i class="fa-solid fa-arrow-down" />
        </div>
        <div class="knockout-stage placement">
          <h4><i class="fa-solid fa-hand-holding-heart" aria-hidden="true" /> Placement</h4>
          <p>
            Optional <strong>3rd place playoff</strong> or a <strong>charity exhibition</strong> match—flexible
            for your season story.
          </p>
        </div>
      </div>
    </section>

    <section class="section steps-section">
      <header class="section-head" data-aos="fade-up">
        <h2>Season flow</h2>
        <p>From group tables to lifting the trophy.</p>
      </header>
      <ol class="steps-track">
        <li
          v-for="(step, i) in steps"
          :key="step.title"
          class="step-card"
          data-aos="fade-up"
          :data-aos-delay="i * 100"
        >
          <span class="step-num">{{ i + 1 }}</span>
          <i :class="step.icon" aria-hidden="true" />
          <h3>{{ step.title }}</h3>
          <p>{{ step.text }}</p>
        </li>
      </ol>
    </section>

    <section class="cta-band" data-aos="fade-up">
      <div class="cta-inner">
        <div class="cta-copy">
          <h2>Built for IPA Match Hub</h2>
          <p>Register your team, manage invites, and organize fixtures—then bring that energy to the league.</p>
        </div>
        <div class="cta-actions">
          <button type="button" class="btn btn-dark-outline" @click="onRegisterTeamClick">
            <i class="fa-solid fa-shield-halved" aria-hidden="true" />
            Register team
          </button>
          <p v-if="editionNoticeVisible" class="edition-notice" role="status">
            <i class="fa-solid fa-hourglass-half" aria-hidden="true" />
            1st Edition Coming Soon
          </p>
        </div>
      </div>
    </section>

    <section class="edition-launch" aria-labelledby="edition-launch-heading">
      <div class="edition-launch__glow" aria-hidden="true" />
      <div class="edition-launch__inner">
        <p class="edition-launch__kicker">1st edition</p>
        <h2 id="edition-launch-heading" class="edition-launch__title">Kickoff is approaching</h2>
        <p class="edition-launch__tagline">
          The inaugural IPromise League season begins
          <time datetime="2026-11-21">21 November 2026</time>.
          Ten teams. Two groups. One champion.
        </p>

        <div
          v-if="editionLaunchHasPassed"
          class="edition-launch__live"
          role="status"
        >
          <i class="fa-solid fa-futbol" aria-hidden="true" />
          <p>The 1st edition is underway.</p>
        </div>

        <div
          v-else
          class="edition-countdown"
          role="timer"
          :aria-label="editionCountdownAriaLabel"
        >
          <div
            v-for="unit in editionCountdownUnits"
            :key="unit.label"
            class="edition-countdown__unit"
            :class="{ 'edition-countdown__unit--seconds': unit.label === 'Seconds' }"
          >
            <span class="edition-countdown__value" aria-hidden="true">{{ pad(unit.value) }}</span>
            <span class="edition-countdown__label">{{ unit.label }}</span>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import AOS from "aos";
import "aos/dist/aos.css";
import leagueSoccer from "@/assets/images/league/soccer.png";
import leagueTrophy from "@/assets/images/league/trophy.jpg";
import leagueTrophy2 from "@/assets/images/league/trophy2.jpg";

const LEAGUE_EDITION_LAUNCH_MS = new Date("2026-11-21T00:00:00+03:00").getTime();

export default {
  name: "IpromiseLeaguePage",

  data() {
    return {
      leagueSoccer,
      leagueTrophy,
      leagueTrophy2,
      highlights: [
        { icon: "fa-solid fa-users", label: "7-a-side" },
        { icon: "fa-solid fa-layer-group", label: "5 teams per group" },
        { icon: "fa-solid fa-table-columns", label: "2 groups (A & B)" },
        { icon: "fa-solid fa-rotate", label: "Group round-robin" },
        { icon: "fa-solid fa-medal", label: "Semis & final" },
        { icon: "fa-solid fa-ranking-star", label: "Optional 3rd / charity" },
      ],
      specCards: [
        {
          icon: "fa-solid fa-futbol",
          title: "Tournament type",
          desc: "7-a-side football league—fast, technical, and built for squad depth.",
        },
        {
          icon: "fa-solid fa-people-group",
          title: "Teams per group",
          desc: "Five teams in each group. Everyone plays everyone once: four group matches per side.",
        },
        {
          icon: "fa-solid fa-table-list",
          title: "Groups",
          desc: "Two groups—Group A and Group B—seeding the knockout bracket.",
        },
        {
          icon: "fa-solid fa-calendar-week",
          title: "Group stage",
          desc: "Round-robin within each group. Points and tie-breakers decide who advances.",
        },
        {
          icon: "fa-solid fa-trophy",
          title: "Knockout stage",
          desc: "Top two from each group reach semi-finals; winners contest the final.",
        },
        {
          icon: "fa-solid fa-heart",
          title: "Placement matches",
          desc: "Optional third-place playoff or a charity exhibition to close the season with purpose.",
        },
      ],
      formatPoints: [
        "Group stage: round-robin in Group A and Group B (4 matches per team).",
        "Knockout: 1st vs 2nd cross-group semis, then the final between semi winners.",
        "Placement: schedule a bronze match or a one-off charity game—your call.",
      ],
      steps: [
        {
          icon: "fa-solid fa-flag",
          title: "Group stage",
          text: "Ten teams split across A & B; full round-robin sets the table.",
        },
        {
          icon: "fa-solid fa-filter",
          title: "Qualify",
          text: "Top two from each group lock in semi-final spots.",
        },
        {
          icon: "fa-solid fa-bolt",
          title: "Knockouts",
          text: "Two semis, then the final for the championship.",
        },
        {
          icon: "fa-solid fa-star",
          title: "Extras",
          text: "Add a 3rd place game or charity exhibition to celebrate the season.",
        },
      ],
      editionNoticeVisible: false,
      countdown: { days: 0, hours: 0, minutes: 0, seconds: 0 },
      editionLaunchHasPassed: false,
      countdownTimer: null,
    };
  },

  computed: {
    editionCountdownUnits() {
      return [
        { value: this.countdown.days, label: "Days" },
        { value: this.countdown.hours, label: "Hours" },
        { value: this.countdown.minutes, label: "Minutes" },
        { value: this.countdown.seconds, label: "Seconds" },
      ];
    },
    editionCountdownAriaLabel() {
      const { days, hours, minutes, seconds } = this.countdown;
      return `${days} days, ${hours} hours, ${minutes} minutes, and ${seconds} seconds until the IPromise League 1st edition on 21 November 2026`;
    },
    splitSoccerStyle() {
      return {
        backgroundImage: `linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%), url(${this.leagueSoccer})`,
      };
    },
  },

  mounted() {
    AOS.init({
      duration: 850,
      once: true,
      offset: 40,
      easing: "ease-out-cubic",
    });
    this.updateEditionCountdown();
    this.countdownTimer = setInterval(this.updateEditionCountdown, 1000);
  },

  beforeUnmount() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  },

  methods: {
    onRegisterTeamClick() {
      this.editionNoticeVisible = true;
    },
    updateEditionCountdown() {
      const diff = LEAGUE_EDITION_LAUNCH_MS - Date.now();
      if (diff <= 0) {
        this.editionLaunchHasPassed = true;
        if (this.countdownTimer) {
          clearInterval(this.countdownTimer);
          this.countdownTimer = null;
        }
        return;
      }

      const totalSeconds = Math.floor(diff / 1000);
      this.countdown = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60,
      };
    },
    pad(value) {
      return String(value).padStart(2, "0");
    },
  },
};
</script>

<style scoped>
.ipromise-league {
  --league-accent: var(--border-color-hover);
  --league-glow: rgba(0, 255, 255, 0.35);
  --league-page-gutter: clamp(16px, 4vw, 28px);
  font-family: "Poppins", system-ui, sans-serif;
  color: var(--text-color1);
  background: var(--background-color2, #0a0f1a);
  flex: 1;
  width: 100%;
  max-width: 100%;
  overflow-x: clip;
  box-sizing: border-box;
}

.ipromise-league *,
.ipromise-league *::before,
.ipromise-league *::after {
  box-sizing: border-box;
}

.ipromise-league i {
  color: var(--border-color-hover);
}

.hero {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background: var(--background-color2, #0a0f1a);
}

.hero-visual {
  margin: 0;
  width: 100%;
  line-height: 0;
  overflow: hidden;
  background: var(--background-color1, #0a0a0a);
}

.hero-visual__img {
  display: block;
  width: 100%;
  height: auto;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  object-position: center center;
}

.hero-intro {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: min(900px, 100% - 2rem);
  margin: 0 auto;
  padding: clamp(24px, 5vw, 56px) var(--league-page-gutter)
    clamp(20px, 4vw, 40px);
  padding-left: max(var(--league-page-gutter), env(safe-area-inset-left, 0px));
  padding-right: max(var(--league-page-gutter), env(safe-area-inset-right, 0px));
  text-align: center;
  animation: heroIntroFade 0.9s ease-out both;
}

@keyframes heroIntroFade {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: clamp(0.72rem, 2vw, 0.85rem);
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--border-color-hover);
  margin-bottom: 14px;
}

.hero-title {
  font-size: clamp(2rem, 5.5vw, 3.35rem);
  font-weight: 900;
  line-height: 1.12;
  color: var(--text-color1);
}

.hero-sub {
  margin-top: 18px;
  font-size: clamp(1rem, 2.1vw, 1.2rem);
  line-height: 1.65;
  color: var(--text-color2, #94a3b8);
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: clamp(12px, 2.5vw, 14px) clamp(20px, 4vw, 26px);
  border-radius: 999px;
  font-weight: 600;
  font-size: clamp(0.88rem, 2vw, 0.95rem);
  border: none;
  cursor: pointer;
  min-height: 44px;
  transition:
    transform 0.28s ease,
    box-shadow 0.28s ease,
    background 0.28s ease,
    border-color 0.28s ease;
}

.btn-primary {
  background: linear-gradient(135deg, var(--border-color-hover), #40e0e0);
  color: #0a1628;
  box-shadow: 0 8px 28px rgba(0, 255, 255, 0.35);
  border: 1px solid var(--border-color-hover);
}

.btn-text-color3,
.btn-text-color3 i {
  color: var(--text-color3);
}

.btn-primary:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 14px 36px rgba(0, 255, 255, 0.45);
}

.btn-ghost {
  background: rgba(255, 255, 255, 0.1);
  color: #f1f5f9;
  border: 1px solid var(--border-color-hover);
  backdrop-filter: blur(8px);
}

.btn-ghost:hover {
  background: rgba(0, 255, 255, 0.12);
  transform: translateY(-3px);
}

.highlights {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(8px, 2vw, 12px);
  padding: clamp(20px, 4vw, 28px) var(--league-page-gutter) 8px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
}

.highlight-pill {
  display: inline-flex;
  align-items: center;
  gap: clamp(6px, 1.5vw, 10px);
  padding: clamp(10px, 2vw, 12px) clamp(14px, 3vw, 20px);
  border-radius: 999px;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.06), rgba(255, 255, 255, 0.02));
  border: 1px solid var(--border-color-hover);
  color: var(--text-color1);
  font-size: clamp(0.78rem, 2vw, 0.9rem);
  font-weight: 600;
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  max-width: 100%;
  text-align: left;
}

.highlight-pill:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.2);
}

.section {
  padding: clamp(40px, 7vw, 72px) var(--league-page-gutter);
  max-width: 1180px;
  margin: 0 auto;
  width: 100%;
}

.league-gallery {
  padding-top: 48px;
  padding-bottom: 32px;
}

.gallery-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: clamp(14px, 3vw, 22px);
}

.gallery-card {
  margin: 0;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid var(--border-color-hover);
  background: rgba(255, 255, 255, 0.03);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.25);
}

.gallery-card img {
  width: 100%;
  height: clamp(160px, 28vw, 240px);
  object-fit: cover;
  display: block;
}

.gallery-card figcaption {
  padding: clamp(10px, 2vw, 14px) clamp(12px, 2.5vw, 18px);
  font-size: clamp(0.82rem, 2vw, 0.9rem);
  font-weight: 600;
  color: var(--text-color2, #94a3b8);
}

.section-head {
  text-align: center;
  margin-bottom: clamp(28px, 5vw, 44px);
}

.section-head h2 {
  font-size: clamp(1.65rem, 3.5vw, 2.25rem);
  font-weight: 800;
  margin-bottom: 10px;
  color: var(--border-color-hover);
}

.bracket-section .section-head {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color-hover);
}

.section-head p {
  color: var(--text-color2, #94a3b8);
  font-size: clamp(0.92rem, 2vw, 1.05rem);
  max-width: min(560px, 100%);
  margin: 0 auto;
  line-height: 1.55;
  padding: 0 0.25rem;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: clamp(14px, 3vw, 22px);
}

.feature-card {
  position: relative;
  padding: clamp(20px, 4vw, 28px) clamp(18px, 3vw, 24px) clamp(22px, 4vw, 30px);
  border-radius: 20px;
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid var(--border-color-hover);
  overflow: hidden;
  transition: transform 0.35s cubic-bezier(0.34, 1.3, 0.64, 1), box-shadow 0.35s ease;
}

.feature-card:hover {
  transform: translateY(-10px) scale(1.01);
  box-shadow: 0 24px 50px rgba(0, 0, 0, 0.28);
}

.feature-icon-wrap {
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  background: linear-gradient(135deg, rgba(0, 255, 255, 0.18), rgba(0, 255, 255, 0.08));
  border: 1px solid var(--border-color-hover);
}

.feature-icon-wrap i {
  font-size: 1.45rem;
}

.feature-card h3 {
  font-size: clamp(1rem, 2.2vw, 1.15rem);
  font-weight: 700;
  margin-bottom: 10px;
  color: var(--text-color1);
}

.feature-card p {
  font-size: clamp(0.85rem, 1.9vw, 0.92rem);
  line-height: 1.6;
  color: var(--text-color2, #94a3b8);
  margin: 0;
}

.split-showcase {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  width: min(1180px, calc(100% - 2 * var(--league-page-gutter)));
  max-width: 100%;
  margin: clamp(16px, 3vw, 24px) auto 0;
  border-radius: clamp(16px, 3vw, 24px);
  overflow: hidden;
  border: 1px solid var(--border-color-hover);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}

.split-visual {
  min-height: clamp(220px, 38vw, 340px);
  background-size: cover;
  background-position: center;
  transition: transform 8s ease-out;
}

.split-showcase:hover .split-visual {
  transform: scale(1.06);
}

.split-copy {
  padding: clamp(28px, 5vw, 48px) clamp(20px, 4vw, 40px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.88));
}

.split-copy h2 {
  font-size: clamp(1.5rem, 3vw, 2rem);
  font-weight: 800;
  margin-bottom: 20px;
  color: #f8fafc;
}

.split-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.split-list li {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
  font-size: clamp(0.88rem, 2vw, 0.98rem);
  line-height: 1.55;
  color: #cbd5e1;
  word-break: break-word;
}

.split-list i {
  margin-top: 3px;
  flex-shrink: 0;
}

.groups-knockout {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
  gap: clamp(14px, 3vw, 22px);
  margin-bottom: clamp(24px, 4vw, 36px);
}

.group-panel {
  padding: clamp(18px, 3vw, 24px);
  border-radius: 18px;
  border: 1px solid var(--border-color-hover);
  background: rgba(255, 255, 255, 0.03);
}

.group-panel h3 {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  font-size: clamp(1.05rem, 2.5vw, 1.2rem);
  margin: 0 0 8px;
  color: var(--text-color1);
}

.group-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  font-weight: 800;
  font-size: 1rem;
  color: #0a1628;
  background: linear-gradient(135deg, var(--border-color-hover), #40e0e0);
}

.group-meta {
  margin: 0 0 16px;
  font-size: 0.9rem;
  color: var(--text-color2, #94a3b8);
}

.group-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.group-list li {
  padding: 10px 14px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: rgba(0, 255, 255, 0.06);
  border: 1px solid rgba(0, 255, 255, 0.12);
  font-size: 0.9rem;
  color: var(--text-color1);
}

.knockout-flow {
  max-width: min(640px, 100%);
  margin: 0 auto;
  text-align: center;
  padding: 0 0.25rem;
}

.knockout-stage {
  padding: clamp(16px, 3vw, 22px);
  border-radius: 18px;
  border: 1px solid var(--border-color-hover);
  background: linear-gradient(165deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
}

.knockout-stage h4 {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 0 0 10px;
  font-size: clamp(0.98rem, 2.2vw, 1.1rem);
  color: var(--text-color1);
}

.knockout-stage p {
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.55;
  color: var(--text-color2, #94a3b8);
}

.knockout-matches {
  list-style: none;
  padding: 0;
  margin: 12px 0 0;
  text-align: left;
}

.knockout-matches li {
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 10px;
  background: rgba(0, 0, 0, 0.2);
  font-size: 0.88rem;
  color: #e2e8f0;
}

.knockout-final {
  border-width: 2px;
}

.placement {
  background: rgba(0, 255, 255, 0.04);
}

.knockout-arrow {
  padding: 8px 0;
  color: var(--border-color-hover);
  font-size: 1.25rem;
}

.steps-section {
  padding-top: 40px;
}

.steps-section .section-head {
  padding-bottom: 20px;
  border-bottom: 1px solid var(--border-color-hover);
}

.steps-track {
  list-style: none;
  margin: 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr));
  gap: clamp(12px, 2.5vw, 18px);
}

.step-card {
  position: relative;
  padding: clamp(20px, 4vw, 26px) clamp(14px, 3vw, 18px);
  border-radius: 18px;
  text-align: center;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid var(--border-color-hover);
  transition: transform 0.3s ease;
}

.step-card:hover {
  transform: translateY(-6px);
}

.step-num {
  position: absolute;
  top: 12px;
  left: 12px;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--border-color-hover), #40e0e0);
  color: #0a1628;
  border: 1px solid var(--border-color-hover);
  font-size: 0.8rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
}

.step-card > i {
  font-size: 1.6rem;
  margin: 8px 0 14px;
  display: block;
}

.step-card h3 {
  font-size: clamp(0.95rem, 2.2vw, 1.05rem);
  font-weight: 700;
  margin-bottom: 8px;
  color: var(--text-color1);
}

.step-card p {
  font-size: clamp(0.8rem, 1.9vw, 0.85rem);
  color: var(--text-color2, #94a3b8);
  line-height: 1.5;
  margin: 0;
}

.cta-band {
  width: min(1100px, calc(100% - 2 * var(--league-page-gutter)));
  margin: clamp(32px, 5vw, 48px) auto clamp(40px, 6vw, 64px);
  border-radius: clamp(16px, 3vw, 24px);
  padding: 2px;
  background: linear-gradient(120deg, rgba(0, 255, 255, 0.45), rgba(0, 200, 220, 0.35), rgba(0, 255, 255, 0.4));
}

.cta-inner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: clamp(16px, 3vw, 24px);
  padding: clamp(28px, 5vw, 40px) clamp(20px, 4vw, 36px);
  border-radius: clamp(14px, 2.8vw, 22px);
  background: linear-gradient(160deg, #0f172a 0%, #020617 100%);
}

.cta-copy {
  flex: 1 1 240px;
  min-width: 0;
}

.cta-copy p {
  margin: 0;
  color: #94a3b8;
  font-size: clamp(0.9rem, 2vw, 1rem);
  max-width: 42ch;
  line-height: 1.55;
}

.cta-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.cta-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 14px;
  flex: 0 1 auto;
  min-width: min(100%, 220px);
}

.cta-copy h2 {
  font-size: clamp(1.35rem, 3.5vw, 2rem);
  font-weight: 800;
  color: #f8fafc;
  margin: 0 0 8px;
  line-height: 1.2;
  word-break: break-word;
}

.edition-notice {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  padding: 12px 18px;
  border-radius: 999px;
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--border-color-hover);
  background: color-mix(in srgb, var(--border-color-hover) 12%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 35%, transparent);
  animation: editionNoticeIn 0.35s ease-out both;
}

@keyframes editionNoticeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.cta-buttons a.btn {
  text-decoration: none;
}

.btn-dark-outline {
  background: transparent;
  color: #e2e8f0;
  border: 2px solid var(--border-color-hover);
}

.btn-dark-outline:hover {
  color: var(--border-color-hover);
  transform: translateY(-3px);
}

.edition-launch {
  position: relative;
  width: min(1100px, calc(100% - 2 * var(--league-page-gutter)));
  max-width: 100%;
  margin: 0 auto clamp(40px, 6vw, 64px);
  padding: clamp(32px, 6vw, 56px) clamp(20px, 4vw, 40px);
  padding-bottom: max(clamp(32px, 6vw, 56px), env(safe-area-inset-bottom, 0px));
  text-align: center;
  color: #f8fafc;
  border-radius: clamp(16px, 3vw, 24px);
  overflow: hidden;
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 35%, transparent);
  background: linear-gradient(
    145deg,
    rgba(6, 12, 28, 0.95) 0%,
    rgba(12, 36, 62, 0.92) 50%,
    rgba(8, 22, 44, 0.95) 100%
  );
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.35);
}

.edition-launch__glow {
  position: absolute;
  inset: -40%;
  background:
    radial-gradient(circle at 30% 50%, color-mix(in srgb, var(--border-color-hover) 22%, transparent), transparent 55%),
    radial-gradient(circle at 70% 40%, rgba(0, 255, 255, 0.08), transparent 50%);
  animation: editionGlowPulse 6s ease-in-out infinite;
  pointer-events: none;
}

@keyframes editionGlowPulse {
  0%, 100% { opacity: 0.55; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.04); }
}

.edition-launch__inner {
  position: relative;
  z-index: 1;
  max-width: 820px;
  margin: 0 auto;
}

.edition-launch__kicker {
  margin: 0 0 12px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--border-color-hover);
}

.edition-launch__title {
  margin: 0 0 16px;
  font-size: clamp(1.5rem, 4vw, 2.2rem);
  font-weight: 900;
  line-height: 1.15;
}

.edition-launch__tagline {
  margin: 0 auto 32px;
  max-width: 52ch;
  font-size: clamp(0.95rem, 2vw, 1.08rem);
  line-height: 1.65;
  color: #94a3b8;
}

.edition-launch__tagline time {
  font-weight: 700;
  color: var(--border-color-hover);
}

.edition-countdown {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: clamp(12px, 2.5vw, 20px);
}

.edition-countdown__unit {
  flex: 1 1 72px;
  min-width: 72px;
  max-width: 120px;
  padding: clamp(16px, 3vw, 22px) clamp(10px, 2vw, 14px);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
}

.edition-countdown__unit--seconds {
  border-color: color-mix(in srgb, var(--border-color-hover) 40%, transparent);
  animation: editionSecondsTick 1s ease-in-out infinite;
}

@keyframes editionSecondsTick {
  0%, 100% { box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2); }
  50% { box-shadow: 0 8px 28px color-mix(in srgb, var(--border-color-hover) 28%, transparent); }
}

.edition-countdown__value {
  display: block;
  font-size: clamp(1.75rem, 5vw, 2.75rem);
  font-weight: 900;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  margin-bottom: 8px;
  color: #fff;
  text-shadow: 0 0 24px color-mix(in srgb, var(--border-color-hover) 35%, transparent);
}

.edition-countdown__label {
  display: block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.75;
}

.edition-launch__live {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  padding: 24px;
  border-radius: 16px;
  background: color-mix(in srgb, var(--border-color-hover) 15%, transparent);
  border: 1px solid color-mix(in srgb, var(--border-color-hover) 40%, transparent);
}

.edition-launch__live i {
  font-size: 2rem;
}

.edition-launch__live p {
  margin: 0;
  font-size: clamp(1.05rem, 2.5vw, 1.25rem);
  font-weight: 700;
}

@media (max-width: 1200px) {
  .steps-track {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 1024px) {
  .gallery-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .split-showcase {
    grid-template-columns: 1fr;
    width: min(1180px, calc(100% - 2 * var(--league-page-gutter)));
  }

  .split-visual {
    min-height: clamp(200px, 42vw, 280px);
  }
}

@media (max-width: 768px) {
  .hero-visual__img {
    aspect-ratio: 16 / 10;
    max-height: min(72vh, 480px);
    object-fit: cover;
    object-position: center center;
  }

  .highlights {
    justify-content: flex-start;
  }

  .highlight-pill {
    flex: 1 1 calc(50% - 8px);
    justify-content: center;
    text-align: center;
  }

  .gallery-row {
    grid-template-columns: 1fr;
  }

  .gallery-card img {
    height: clamp(180px, 45vw, 220px);
  }

  .section {
    padding: clamp(36px, 6vw, 48px) var(--league-page-gutter);
  }

  .league-gallery {
    padding-top: clamp(28px, 5vw, 40px);
  }

  .feature-icon-wrap {
    width: clamp(48px, 12vw, 56px);
    height: clamp(48px, 12vw, 56px);
  }

  .knockout-matches li {
    font-size: clamp(0.82rem, 2vw, 0.88rem);
  }

  .steps-track {
    grid-template-columns: 1fr;
  }

  .cta-inner {
    flex-direction: column;
    align-items: stretch;
    text-align: center;
  }

  .cta-copy {
    flex: 1 1 auto;
  }

  .cta-copy p {
    max-width: none;
    margin: 0 auto;
  }

  .cta-actions {
    align-items: stretch;
    width: 100%;
  }

  .btn,
  .btn-dark-outline {
    width: 100%;
  }

  .edition-notice {
    justify-content: center;
    width: 100%;
    text-align: center;
    font-size: clamp(0.85rem, 2vw, 0.95rem);
  }

  .edition-countdown__unit {
    min-width: calc(50% - 10px);
    max-width: none;
    flex: 1 1 calc(50% - 10px);
  }
}

@media (max-width: 480px) {
  .hero-title {
    font-size: clamp(1.65rem, 8vw, 2rem);
  }

  .hero-sub {
    font-size: clamp(0.92rem, 3.8vw, 1rem);
  }

  .highlight-pill {
    flex: 1 1 100%;
    width: 100%;
  }

  .gallery-card,
  .feature-card {
    border-radius: 16px;
  }

  .group-panel,
  .knockout-stage,
  .step-card {
    border-radius: 14px;
  }

  .edition-countdown__unit {
    min-width: calc(50% - 8px);
    padding: 14px 10px;
  }

  .edition-countdown__value {
    font-size: clamp(1.5rem, 7vw, 2rem);
  }

  .edition-countdown__label {
    font-size: 0.65rem;
    letter-spacing: 0.08em;
  }
}

@media (max-width: 360px) {
  .hero-visual__img {
    max-height: 56vh;
  }

  .edition-countdown {
    gap: 8px;
  }

  .edition-countdown__unit {
    min-width: calc(50% - 4px);
    border-radius: 12px;
  }
}

@media (hover: none) and (pointer: coarse) {
  .feature-card:hover,
  .highlight-pill:hover,
  .step-card:hover,
  .btn:hover,
  .btn-dark-outline:hover {
    transform: none;
  }

  .split-showcase:hover .split-visual {
    transform: none;
  }
}

@media (orientation: landscape) and (max-height: 520px) {
  .hero-visual__img {
    max-height: 70vh;
    aspect-ratio: auto;
  }

  .hero-intro {
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .hero-intro,
  .edition-launch__glow,
  .edition-countdown__unit--seconds,
  .feature-card,
  .split-visual,
  .step-card {
    animation: none !important;
    transition-duration: 0.01ms !important;
  }

  .feature-card:hover,
  .highlight-pill:hover,
  .btn:hover {
    transform: none;
  }
}
</style>
